// Item video helpers — shared by the traveller preview (`/preview/$id`) and the
// customer-preview discussion view, so the two resolve providers identically.
// They previously lived only in the preview app; the collab card had no video
// support at all, and duplicating these to give it some would have guaranteed
// the two drifted (XT-532).

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

// Videos may be stored as `string[]`, `{ url }[]`, or `{ src }[]`. The
// item card only cares about URL strings, so collapse all three shapes
// into a single flat list.
export const narrowVideos = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const entry of raw) {
    const direct = asString(entry);
    if (direct) {
      out.push(direct);
      continue;
    }
    if (isObject(entry)) {
      const url = asString(entry.url) ?? asString(entry.src);
      if (url) out.push(url);
    }
  }
  return out;
};

// Direct port of `travel_planner_front/lib/utils.ts:getVideoEmbedUrl`.
// Returns the embeddable form for YouTube / Vimeo / Wistia and falls
// through unchanged for direct media URLs.
export const getVideoEmbedUrl = (videoUrl: string): string => {
  if (!videoUrl) return videoUrl;
  if (
    videoUrl.includes('youtube.com/embed/') ||
    videoUrl.includes('youtu.be/embed/')
  ) {
    return videoUrl;
  }

  if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
    const videoId =
      videoUrl.split('v=')[1]?.split('&')[0] || videoUrl.split('/').pop();
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
  } else if (videoUrl.includes('vimeo.com')) {
    const match = videoUrl.match(/vimeo\.com\/(\d+)(?:\/(\w+))?/);
    if (match) {
      const videoId = match[1];
      const hash = match[2] ? `?h=${match[2]}` : '';
      return `https://player.vimeo.com/video/${videoId}${hash}`;
    }
  } else if (
    videoUrl.includes('wistia.com') ||
    videoUrl.includes('fast.wistia.net')
  ) {
    const pathParts = videoUrl.split('/').pop();
    if (pathParts) {
      const videoId = pathParts.split('?')[0];
      if (videoId) {
        return `https://fast.wistia.net/embed/iframe/${videoId}`;
      }
    }
  }
  // Direct media URLs (mp4/webm) pass through; the item card detects
  // these and renders an HTML <video> instead of an <iframe>.
  return videoUrl;
};

// Direct-video heuristic — true when the URL points at an uploadthing
// CDN file or otherwise ends in a recognised media extension. Lets the
// item card render an HTML <video> tag with controls instead of an
// iframe (iframes don't work for raw mp4/webm sources).
export const isDirectMediaUrl = (url: string): boolean => {
  if (!url) return false;
  if (/utfs\.io\/f\//i.test(url)) return true;
  if (/\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(url)) return true;
  return false;
};
